import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import TextInput from "../../common/textfield/TextInput";
import * as Yup from "yup";
import {
  createAccountAPI,
  getPatientInfo,
  getSingleUser,
  updatePatientDemographics,
} from "../../api/Individual";
import SelectFiled from "../../common/textfield/SelectFiled";
import ReactDatePicker from "../../common/textfield/ReactDatePicker";
import {
  caregions,
  createAccountOptions,
  genderOptions,
  usregions,
} from "./Constants";
import CountrySelect from "../../common/textfield/CountrySelect";
import RegionSelect from "../../common/textfield/RegionSelect";
import { Button } from "../../common/Button";
import UpdateDetailsIcon from "../../../assets/images/update.svg";
import { ViewDate } from "../../../utils/DateSupport";
import { splitContactNumber } from "../../../utils/phoneNumberSupport";
import { decryptData } from "../../EncryptDecrypt";
import { useSelector } from "react-redux";
import { failed, success } from "../../common/Toastify";
import Switch from "@mui/material/Switch";
import { useConfirmDialog } from "../../../hooks/useConfirmDialog";
import { commonDisableAPI } from "../../api/CommonApis";
import { useOrgHcnQuery } from "../../../hooks/ReactQueryHooks/useOrgHcnQuery";
import moment from "moment";
import { getOrganizationSettings } from "../../api/Organization";
import { useGetOrgLicensedProducts } from "../../../hooks/ReactQueryHooks/useGetOrgLicensedProducts";

const EditPatientModal = ({
  patientId,
  onShow,
  onHide,
  hasAccessToEdit,
  organizations,
  patientOrgId
}) => {
  const [patientRecord, setPatientRecord] = useState(null);
  const [btnLoading, setBtnLoading] = useState(false);
  const myOrgId = useSelector(
    (state) => state?.auth?.user?.organizations?.[0]?.id
  );
  const [personRecord, setPersonRecord] = useState(null);
  const { getConfirmation } = useConfirmDialog();
  const [createAccountFlag, setCreateAccountFlag] = useState(false);
  const [callUsersApi, setCallUsersApi] = useState(false);
  const [hcnList, setHcnList] = useState([]);
  const orgId = useSelector((state) => state?.auth?.user?.organizations?.[0]?.id);
  const user = useSelector((state) => state?.auth?.user);
  const isAdmin = user?.["cognito:groups"]?.includes("Super-Admin");

  const [enforceHCN, setEnforceHCN] = useState(false);
  const [enforceEmail, setEnforceEmail] = useState(false);
  const [enforcePhoneNumber, setEnforcePhoneNumber] = useState(false);
  const [enforceAddress, setEnforceAddress] = useState(false);
  const [enforceCountry, setEnforceCountry] = useState(false);
  const [enforceProvince, setEnforceProvince] = useState(false);
  const [enforceCity, setEnforceCity] = useState(false);
  const [enforcePostalCode, setEnforcePostalCode] = useState(false);
  const [enforceDOB, setEnforceDOB] = useState(false);
  const [enforceGender, setEnforceGender] = useState(false);
  const [enforceFirstName, setEnforceFirstName] = useState(false);
  const [enforceLastName, setEnforceLastName] = useState(false);
  const [loading, setLoading] = useState(false);
  const { data: orgLicensedProducts } = useGetOrgLicensedProducts({})
  const is_VirtualCareEnabled = orgLicensedProducts?.virtualCare
  const [ecrEnabled, setEcrEnabled] = useState(false)

  // Disable editing only when ecrEnabled is true AND patientRecord.source is "EXT"
  // Otherwise, allow editing if hasAccessToEdit is true
  const canEdit = hasAccessToEdit && !(ecrEnabled && patientRecord?.source === "EXT")

  const onSuccessHcn = (data) => {
    const hcnOptions = data
      ?.filter((hcn) => hcn.active)
      .sort((a, b) => a.description.localeCompare(b.description))
      .map((hcn) => ({
        ...hcn,
        value: hcn.authority === "#" ? `test-authority-${hcn.id}` : hcn.authority,
        name: hcn?.display || hcn?.description ,
      }));

    setHcnList(hcnOptions);
  };
  useOrgHcnQuery({ onSuccess: onSuccessHcn, orgId: patientOrgId });

  useEffect(() => {
    if (!patientOrgId) return;
    setLoading(true);
    getOrganizationSettings(patientOrgId)
      .then((res) => {
        if (res?.status) {
          const p = res.data?.patient || {};
          setEnforceHCN(!!p.hcn);
          setEnforceEmail(!!p.email);
          setEnforcePhoneNumber(!!p.phone);
          setEnforceCountry(!!p.country);
          setEnforceProvince(!!p.province);
          setEnforceCity(!!p.city);
          setEnforceFirstName(!!p.firstName);
          setEnforceLastName(!!p.lastName);
          setEnforceGender(!!p.gender);
          setEnforceDOB(!!p.dob);
          setEnforcePostalCode(!!p.postalCode);
          setEnforceAddress(!!p.address);
          setEcrEnabled(!!res?.data?.ecrEnabled)
        }
      })
      .catch((res) => {
        failed(
          res?.response?.data?.message ||
          res?.response?.data?.error ||
          res.message
        );
      })
      .finally(() => setLoading(false));
  }, [isAdmin, patientOrgId]);

  const formik = useFormik({
    initialValues: {
      first_name: patientRecord?.name?.[0]?.given?.[0] || "",
      last_name: patientRecord?.name?.[0]?.family || "",
      date_of_birth: patientRecord?.birthDate || "",
      phone_number: "",
      ext: "",
      address: patientRecord?.address?.[0]?.line?.[0] || "",
      city: patientRecord?.address?.[0]?.city || "",
      country: patientRecord?.address?.[0]?.country || "",
      region: patientRecord?.address?.[0]?.state || "",
      email: "",
      organization:
        patientRecord?.managingOrganization?.[0]?.reference?.split("/")?.[1] ||
        "",
      gender: patientRecord?.gender || "",
      postalCode: patientRecord?.address?.[0]?.postalCode || "",
      create_account: patientRecord?.accountCreated || false,
      organization_display: patientRecord?.managingOrganization?.[0]?.display,
      hcn:
        patientRecord?.identifier?.find(
          (identifier) => identifier?.type === "HCN"
        )?.value || "",
      healthCardAuthority: patientRecord?.identifier?.find(
        (identifier) => identifier?.type === "HCN"
      )?.system || "",
    },
    validationSchema: Yup.object({
      first_name: enforceFirstName ? Yup.string()
        .max(50, "Must be 50 characters or less")
        .required("Required first name field")
        .matches(/^$|^\S+.*/, "Only blankspaces is not valid.") :
        Yup.string(),
      last_name: enforceLastName ? Yup.string()
        .max(50, "Must be 50 characters or less")
        .required("Required last name field")
        .matches(/^$|^\S+.*/, "Only blankspaces is not valid.") :
        Yup.string(),
      date_of_birth: enforceDOB
            ? Yup.string()
                .required("Required date of birth field")
                .nullable()
                .test(
                  "valid-format",
                  "Invalid format. Use MMM-DD-YYYY (JAN-01-2024).",
                  function (value) {
                    if (value) { 
                      const isValid1 = moment(value, "MM/DD/YYYY", true).isValid();
                      const isValid2 = moment(value, "MM-DD-YYYY", true).isValid();
                      if (!isValid1 && !isValid2) return false;
                      const dob = isValid1 ? moment(value, "MM/DD/YYYY") : moment(value, "MM-DD-YYYY");
                      if (dob.isAfter(moment())) {
                        return this.createError({
                          message: "Date of birth cannot be in the future.",
                        });
                      }
                      let minDate = moment().subtract(125, "years");
                      if (dob.isBefore(minDate)) {
                        return this.createError({
                          message: "Date of Birth cannot be more than 125 years ago.",
                        });
                      }
                    }
                    return true;
                  }
                )
            : Yup.string()
                .nullable()
                .test(
                  "valid-format",
                  "Invalid format. Use MMM-DD-YYYY (JAN-01-2024).",
                  function (value) {
                    if (!value) return true; 
                    const isValid1 = moment(value, "MM/DD/YYYY", true).isValid();
                    const isValid2 = moment(value, "MM-DD-YYYY", true).isValid();
                    if (!isValid1 && !isValid2) return false;
                    const dob = isValid1 ? moment(value, "MM/DD/YYYY") : moment(value, "MM-DD-YYYY");
                    if (dob.isAfter(moment())) {
                      return this.createError({
                        message: "Date of birth cannot be in the future.",
                      });
                    }
                    let minDate = moment().subtract(125, "years");
                    if (dob.isBefore(minDate)) {
                      return this.createError({
                        message: "Date of Birth cannot be more than 125 years ago.",
                      });
                    } 
                    return true;
                  }
                ),
      phone_number: enforcePhoneNumber
        ? Yup.string()
            .required("Required phone number field")
            .matches(
              /^(?:\+?1[-. ]?)?\(?\d{3}\)?[-. ]?\d{3}[-. ]?\d{4}$/,
              "Enter Valid Phone Number."
            )
        : Yup.string().test(
            "is-valid-or-empty-placeholder",
            "Enter Valid Phone Number.",
            (value) => {
              if (!value) return true; // allow empty
              if (value.trim() === "(___) ___-____") return true; // allow placeholder
              return /^(?:\+?1[-. ]?)?\(?\d{3}\)?[-. ]?\d{3}[-. ]?\d{4}$/.test(value);
            }
      ),
      // gender: Yup.string()
      // .required("Required gender field")
      // .matches(/^$|^\S+.*/, "Only blankspaces is not valid."),
      ext: Yup.string().matches(/^\d{0,4}$/, "Must be 4 digit or less."),
      address: enforceAddress ? Yup.string().matches(
        /^$|^\S+.*/,
        "Only blankspaces is not valid."
      ) : Yup.string(),
      city: enforceCity ? Yup.string()
        .required("Required city field")
        .matches(/^$|^\S+.*/, "Only blankspaces is not valid.") : Yup.string(),
      email: enforceEmail ?
          !patientRecord?.accountCreated &&
          createAccountFlag &&
        Yup.string()
          .email("Invalid email address")
          .required("Required email field")
          .matches(/^$|^\S+.*/, "Only blankspaces is not valid.") :
        Yup.string()
          .email("Invalid email address")
          .matches(/^$|^\S+.*/, "Only blankspaces is not valid."),
      country: enforceCountry ? Yup.string()
        .required("Required country field")
        .matches(/^$|^\S+.*/, "Only blankspaces is not valid.") : Yup.string(),
      region: enforceProvince ? Yup.string()
        .required("Required region field")
        .matches(/^$|^\S+.*/, "Only blankspaces is not valid.") : Yup.string(),
      postalCode: enforcePostalCode ? Yup.string().when(["country"], (country) =>
        country === "US"
          ? Yup.number()
            .typeError("Zip code must be a number")
            .required("Required field")
          : Yup.string()
            .required("Required field")
            .matches(
              /^[A-Za-z]\d[A-Za-z] [0-9][A-Za-z]\d$/,
              "Postal code must be in A1A 1A1 format."
            )
      ) : Yup.string().when(["country"], (country) =>
        country === "US"
          ? Yup.number()
            .typeError("Zip code must be a number")
          : Yup.string()
            .matches(
              /^[A-Za-z]\d[A-Za-z] [0-9][A-Za-z]\d$/,
              "Postal code must be in A1A 1A1 format."
            )
      ),
      hcn: enforceHCN ? Yup.string()
        .matches(/^\d+$/, "Patient Identifier must contain only numbers")
        .required("Patient Identifier is required") : Yup.string(),
      healthCardAuthority: Yup.string().when("hcn", {
        is: (hcn) => hcn && hcn.length > 0,
        then: (schema) => schema
          .required("Identifier Type is required when Patient Identifier is provided")
          .matches(/^\S+.*/, "Only blankspaces is not valid."),
      }),
    }),
    onSubmit: (values) => {
      setBtnLoading(true);
      const dob = moment(values?.date_of_birth).format("MM-DD-YYYY")
      let payload = {
        birthDate: (dob !== "Invalid date") ? dob : "",
        firstName: values?.first_name,
        lastName: values?.last_name,
        gender: values?.gender,
        healthCardNumber: values?.hcn,
        managingOrganization:
          values?.organization?.[0]?.reference?.split("/")[1],
        phone: values?.phone_number,
        email: values?.email,
        address: [
          {
            city: values?.city,
            country: values?.country,
            postalCode: values?.postalCode,
            state: values?.country + "-" + values?.region,
            use: "home",
            line: [`${values?.address}`],
          },
        ],
        healthCardAuthority: values?.healthCardAuthority
      };

      if (values?.ext) {
        payload.phone = values?.phone_number + " ext. " + values?.ext;
      }

      let payloadForUsers = {
        name: [
          {
            use: "usual",
            text: values?.first_name + " " + values?.last_name,
            family: values?.last_name,
            given: [values?.first_name],
          },
        ],
        gender: values?.gender,
        birthDate: values?.date_of_birth,
        telecom: [
          {
            system: "email",
            value: values?.email?.toLowerCase(),
            use: "work",
          },
        ],
        address: [
          {
            city: values?.city,
            country: values?.country,
            postalCode: values?.postalCode,
            state: ((values?.country + "-" + values?.region) === "undefined-undefined") ? "" : (values?.country + "-" + values?.region),
            use: "home",
            line: [`${values?.address ? values?.address : ""}`],
          },
        ],
        email: values?.email?.toLowerCase(),
        managingOrganization: values?.organization,
        generalPractitioner: [],
        type: ["Patient"],
        healthCardNumber: values?.hcn,
        id: personRecord?.id,
        createAccount: true,
        healthCardAuthority: values?.healthCardAuthority
      };

      if (values?.phone_number) {
        payloadForUsers.telecom.push({
          systemm: "phone",
          use: "work",
          value: values?.phone_number,
        });
      }

      if (callUsersApi) {
        createAccountAPI(myOrgId, payloadForUsers).then((res) => {
          if (res?.status === true) {
            updatePatientDemographics(patientId, myOrgId, payload)
              .then((res) => {
                if (res?.status === true) {
                  success(res?.message);
                  onHide();
                }
              })
              .catch((res) => failed(res?.message || res?.result?.message))
              .finally(() => setBtnLoading(false));
          }
        });
      } else {
        updatePatientDemographics(patientId, myOrgId, payload)
          .then((res) => {
            if (res?.status === true) {
              success(res?.message);
              onHide();
            }
          })
          .catch((res) => {
            let message = res?.response?.data?.message || res?.response?.data?.error || res?.message;
            failed(message)
          })
          .finally(() => setBtnLoading(false));
      }
    },
  });

  useEffect(() => {
    if (patientId) {
      getPatientInfo(patientId).then((res) => {
        if (res?.result) {
          getSingleUser({
            id: patientId,
            type: "Patient",
            orgId: myOrgId,
          }).then((res) => {
            if (res?.status === true && res?.data) {
              setPersonRecord(res?.data);
            }
          });
          if (typeof res?.result?.accountCreated === "string") {
            setCreateAccountFlag(res?.result?.accountCreated === "true");
          }
          else 
            setCreateAccountFlag(res?.result?.accountCreated);

          let countryRegion = res?.result?.address?.[0]?.state ==="undefined-undefined" ? "" : res?.result?.address?.[0]?.state?.split("-");
          setPatientRecord(res?.result);
          formik.setFieldValue(
            "first_name",
            res?.result?.name?.[0]?.given?.[0]
          );
          formik.setFieldValue("last_name", res?.result?.name?.[0]?.family);
          formik.setFieldValue("date_of_birth", res?.result?.birthDate);
          formik.setFieldValue(
            "hcn",
            res?.result?.identifier?.find(
              (identifier) => identifier?.type === "HCN"
            )?.value || ""
          );
          formik.setFieldValue(
            "healthCardAuthority",
            res?.result?.identifier?.find(
              (identifier) => identifier?.type === "HCN"
            )?.system || ""
          );
          formik.setFieldValue("gender", res?.result?.gender);
          formik.setFieldValue("address", res?.result?.address?.[0]?.line?.[0] === "undefined" ? "" : res?.result?.address?.[0]?.line?.[0]);
          formik.setFieldValue("country", res?.result?.address?.[0]?.country);
          formik.setFieldValue("region", countryRegion === "" ? "" : countryRegion?.[1]);
          formik.setFieldValue("city", res?.result?.address?.[0]?.city);
          formik.setFieldValue(
            "postalCode",
            res?.result?.address?.[0]?.postalCode
          );
          formik.setFieldValue(
            "phone_number",
            splitContactNumber(
              decryptData(
                res?.result?.telecom?.find((ele) => ele?.system === "phone")
                  ?.value || ""
              )
            )?.phone_number?.replace(/^\+1/, "")
          );
          formik.setFieldValue(
            "email",
            res?.result?.telecom?.find((num) => num?.system === "email")?.value
          );
          formik.setFieldValue(
            "organization",
            res?.result?.managingOrganization
          );
          formik.setFieldValue(
            "organization_display",
            res?.result?.managingOrganization?.[0]?.display
          );
          formik.setFieldValue("create_account", res?.result?.accountCreated);
          formik.setFieldValue(
            "ext",
            splitContactNumber(
              decryptData(
                res?.result?.telecom?.find((ele) => ele?.system === "phone")
                  ?.value || ""
              )
            )?.ext
          );
        }
      });
    }
  }, [patientId]);

  const canDeactivate = (typeof patientRecord?.accountCreated === "string") ? patientRecord?.accountCreated === "true" : patientRecord?.accountCreated;
  const handlePostalCodeChange = (e) => {
    const { value } = e?.target;
    let spacedValue = value;
    if (formik?.values?.country !== "US") {
      // Remove spaces and non-alphanumeric characters
      const formattedValue = value.replace(/[^A-Za-z0-9]/g, "");
      // Add a space after every 3 letters
      spacedValue = formattedValue.replace(
        /^([A-Za-z0-9]{3})([A-Za-z0-9]+)/,
        "$1 $2"
      );
    }
    // Set the formatted value in the formik
    formik?.setFieldValue("postalCode", spacedValue);
  };

  const handleOrgChange = (event) => {
    formik.setFieldValue("organization", event?.target?.value);
  };

  const handleHcnChange = (event) => {
    formik.setFieldValue("hcn", event?.target?.value);
  }

  const handleCreateAccountChange = (e) => {
    let create = false;
    if(typeof e?.target?.value === "string")
      create = e?.target?.value === "true";
    else
      create = e?.target?.value;

    formik?.setFieldValue("create_account", create);
    setCreateAccountFlag(create);
    setCallUsersApi(create);
  };

  const handleEnableClick = async (id, entryName) => {
    const status = "enable";
    const confirmed = await getConfirmation({
      title: "Attention!",
      entryName,
      actionBtnTitle: "Activate",
      message: "Are you sure you would like to activate ",
    });
    if (confirmed) {
      setBtnLoading(true);
      commonDisableAPI({
        key: "person",
        id,
        status,
        userOrgId: myOrgId,
      })
        .then(() => {
          success("Activated Individual!");
          onHide();
        })
        .catch((res) =>
          failed(
            res?.response?.data?.message ||
            res?.response?.data?.error ||
            res.message
          )
        )
        .finally(() => setBtnLoading(false));
    }
  };

  const handleDelete = async (id, entryName) => {
    const body = {
      key: "person",
      id,
      userOrgId: myOrgId,
    };

    const confirmed = await getConfirmation({
      title: "Attention!",
      entryName,
      actionBtnTitle: "Deactivate",
      message: "Are you sure you would like to deactivate ",
    });

    if (confirmed) {
      setBtnLoading(true);
      commonDisableAPI(body)
        .then(() => {
          success("Individual Deactivated!");
          onHide();
        })
        .catch((res) =>
          failed(
            res?.response?.data?.message ||
            res?.response?.data?.error ||
            res?.message
          )
        )
        .finally(() => {
          setBtnLoading(false);
        });
    }
  };

  const handleSwitch = () => {
    personRecord?.active
      ? handleDelete(personRecord?.id, patientRecord.name?.[0]?.text)
      : handleEnableClick(personRecord?.id, patientRecord.name?.[0]?.text);
  };
  return (
    <Modal
      backdropClassName
      backdrop={"static"}
      show={onShow}
      onHide={onHide}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      className="custom-dialog"
    >
      <Modal.Header closeButton className="border-0">
        <Modal.Title id="contained-modal-title-vcenter">
          Patient Details
        </Modal.Title>
        {
          personRecord && canDeactivate && (
          <div className="">
            <Switch
              checked={personRecord?.active}
              onChange={() => handleSwitch()} // Use the callback function
              color="primary"
              disabled={!canEdit}
            />
          </div>
        )}
      </Modal.Header>
      <Modal.Body>
        <form className="common-form border-fields userAccount">
          <Row>
            <Col>
              <TextInput
                style={{
                  textTransform: "capitalize",
                }}
                type={"text"}
                keyField={"first_name"}
                label={"First Name"}
                formik={formik}
                placeholder={"First Name"}
                required={enforceFirstName}
                hideRequired={!enforceFirstName}
                readOnly={!canEdit}
              />
            </Col>
            <Col>
              <TextInput
                style={{
                  textTransform: "capitalize",
                }}
                type={"text"}
                keyField={"last_name"}
                label={"Last Name"}
                formik={formik}
                placeholder={"Last Name"}
                required={enforceLastName}
                hideRequired={!enforceLastName}
                readOnly={!canEdit}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <ReactDatePicker
                keyField={"date_of_birth"}
                label={"Date of Birth"}
                formik={formik}
                placeholder={"Select Date"}
                hasValue={formik?.values?.["date_of_birth"]}
                displayAge={formik.errors.date_of_birth ? false : true}
                readOnly={!canEdit}
                required={enforceDOB}
                hideRequired={!enforceDOB}
              />
            </Col>
            <Col>
              <SelectFiled
                keyField={"gender"}
                label={"Select Gender"}
                formik={formik}
                options={genderOptions}
                readOnly={!canEdit}
                required={enforceGender}
                hideRequired={!enforceGender}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <TextInput
                style={{
                  textTransform: "capitalize",
                }}
                type={"text"}
                keyField={"hcn"}
                label={"Patient Identifier"}
                formik={formik}
                onChange={handleHcnChange}
                placeholder={"Patient Identifier"}
                readOnly={!canEdit}
                required={enforceHCN}
                hideRequired={!enforceHCN}
              />
            </Col>
            <Col>
              <SelectFiled
                keyField={"healthCardAuthority"}
                label={"Identifier Type"}
                formik={formik}
                options={
                  (() => {
                    const currentAuthority = formik?.values?.healthCardAuthority;
                    const isInList = currentAuthority && hcnList.some((opt) => opt.value === currentAuthority);
                    if (currentAuthority && !isInList) {
                      return [{ value: currentAuthority, name: "—" }, ...hcnList];
                    }
                    return hcnList;
                  })()
                }
                readOnly={!canEdit}
                required={enforceHCN}
                hideRequired={!enforceHCN}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              {patientRecord?.accountCreated || !is_VirtualCareEnabled ? <TextInput
                style={{
                  textTransform: "capitalize",
                }}
                type={"text"}
                keyField={"create_account"}
                label={"Account Created"}
                formik={formik}
                placeholder={"Account Created"}
                required={true}
                readOnly={true}
                disabled={true}
              />
                :
                <SelectFiled
                  keyField={"create_account"}
                  label={"Create Account"}
                  formik={formik}
                  placeholder={"Create Account"}
                  options={createAccountOptions}
                  readOnly={patientRecord?.accountCreated || !canEdit}
                  onChange={handleCreateAccountChange}
                />}
            </Col>
            <Col sm="8">
              <TextInput
                type={"email"}
                keyField={"email"}
                label={"Email Address"}
                formik={formik}
                placeholder={"Email Address"}
                required={
                  (!formik?.values?.create_account && !enforceEmail)
                    ? false
                    : true
                }
                readOnly={!canEdit || (
                  patientRecord?.telecom?.find((num) => num?.system === "email")
                    ?.value && formik?.values?.create_account
                    ? true
                    : false)
                }
                disabled={!canEdit || (patientRecord?.telecom?.find((num) => num?.system === "email")
                  ?.value && formik?.values?.create_account
                  ? true
                  : false)}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <TextInput
                style={{
                  textTransform: "capitalize",
                }}
                type={"address"}
                keyField={"address"}
                label={"Address"}
                formik={formik}
                placeholder={"Address"}
                required={enforceAddress}
                hideRequired={!enforceAddress}
                readOnly={!canEdit}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <CountrySelect
                keyField={"country"}
                label={"Country"}
                required={enforceCountry}
                hideRequired={!enforceCountry}
                formik={formik}
                readOnly={!canEdit}
              />
            </Col>

            <Col>
              <RegionSelect
                defaultOptionLabel={
                  formik?.values?.country === "US"
                    ? "Select State"
                    : "Select Province"
                }
                keyField={"region"}
                label={formik?.values?.country === "US" ? "State" : "Province"}
                formik={formik}
                readOnly={!canEdit}
                required={enforceProvince}
                hideRequired={!enforceProvince}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <TextInput
                style={{
                  textTransform: "capitalize",
                }}
                type={"text"}
                keyField={"city"}
                label={"City"}
                formik={formik}
                placeholder={"City"}
                required={enforceCity}
                hideRequired={!enforceCity}
                readOnly={!canEdit}
              />
            </Col>
            <Col>
              <TextInput
                onChange={handlePostalCodeChange}
                keyField={"postalCode"}
                label={
                  formik?.values?.country === "US" ? "Zip Code" : "Postal Code"
                }
                formik={formik}
                placeholder={
                  formik?.values?.country === "US" ? "Zip Code" : "Postal Code"
                }
                readOnly={!canEdit}
                // max length for both zip code and postal code(plus space)
                maxLength={formik?.values?.country === "US" ? "5" : "7"}
                required={enforcePostalCode}
                hideRequired={!enforcePostalCode}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <TextInput
                style={{
                  textTransform: "capitalize",
                }}
                type={"phone"}
                keyField={"phone_number"}
                label={"Phone Number"}
                formik={formik}
                placeholder={"Phone Number"}
                required={enforcePhoneNumber}
                readOnly={!canEdit}
                hideRequired={!enforcePhoneNumber}
              />
            </Col>
            <Col>
              <TextInput
                style={{
                  textTransform: "capitalize",
                }}
                type={"phone_number"}
                keyField={"ext"}
                label={"Ext."}
                formik={formik}
                placeholder={"Ext."}
                required={false}
                readOnly={!canEdit}
                hideRequired={true}
              />
            </Col>
          </Row>

          <Row>
            <Col>
              <TextInput
                style={{
                  textTransform: "capitalize",
                }}
                type={"text"}
                keyField={"organization_display"}
                label={"Organization"}
                formik={formik}
                placeholder={"Organization"}
                required={true}
                readOnly={true}
                disabled={true}
              />
            </Col>
          </Row>

          <Row>
            <Col>
              {canEdit && (
                <>
                  <Button
                    style={{ marginRight: "20px" }}
                    onClick={() => {
                      onHide();
                    }}
                    variant="secondary"
                    title="Cancel"
                  >
                    Cancel
                  </Button>
                  <Button isLoading={btnLoading} onClick={formik.handleSubmit}>
                    <img src={UpdateDetailsIcon} /> Update
                  </Button>
                </>
              )}
            </Col>

            <Col style={{ display: "flex", justifyContent: "flex-end" }}>
              <div style={{ alignSelf: "flex-end" }}>
                User Created on {ViewDate(patientRecord?.createdAt)}
              </div>
            </Col>
          </Row>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default EditPatientModal;